import { useCallback } from "react";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import type { CTAAction } from "./ThinkingStep/cta-types";
import { isStoryBibleCtaType } from "./storyBibleCta";
import {
  computeToolRunningDisplayProgress,
  toolNameMatchesStoryBibleCta,
} from "./storyBibleCtaProgress";
import { useTimedPhaseDisplayProgress } from "./useTimedPhaseDisplayProgress";

/** Display progress for the Story Bible CTA card (phase events + in-tool time creep). */
export function useStoryBibleCtaDisplayProgress(ctaAction: CTAAction | undefined): number {
  const phaseProgress = usePersistentChatStore((s) => s.storyBibleCtaProgress);
  const toolStartedAt = usePersistentChatStore((s) => s.storyBibleCtaToolStartedAt);
  const toolRuns = usePersistentChatStore((s) => s.toolRuns);
  const active = Boolean(
    ctaAction
    && ctaAction.status === "in_progress"
    && isStoryBibleCtaType(ctaAction.type),
  );
  const ctaType = ctaAction?.type;

  const matchesTool = useCallback(
    (toolName: string) => (ctaType ? toolNameMatchesStoryBibleCta(ctaType, toolName) : false),
    [ctaType],
  );

  const computeToolRunningProgress = useCallback(
    (phase: number, startedAt: number, toolName: string) => {
      if (!ctaType) return phase;
      void toolName;
      return computeToolRunningDisplayProgress(phase, startedAt, ctaType);
    },
    [ctaType],
  );

  return useTimedPhaseDisplayProgress({
    active,
    phaseProgress,
    toolStartedAt,
    toolRuns,
    matchesTool,
    computeToolRunningProgress,
  });
}

/** Generate-cast progress for Story Bible UI that should match the floating chat CTA card. */
export function useGenerateCastCtaDisplayProgress(): { active: boolean; progress: number } {
  const ctaAction = usePersistentChatStore((s) => s.ctaAction);
  const active = ctaAction?.type === "generate_cast" && ctaAction.status === "in_progress";
  const progress = useStoryBibleCtaDisplayProgress(active ? ctaAction : undefined);
  return { active, progress };
}
